import {
  DynamicPricesDto,
  DynamicPricesModel,
  ReferencePriceDto,
  ReferencePricesPostDto,
  ReferencePriceModel,
  DynamicEmsSettingsDto,
  DynamicEmsSettingsModel,
  UpdateDynamicEmsSettingsDto,
  UpdateDynamicEmsSettingsModel,
} from '../../types'
import { formatStringToNumberWithFallback } from '../../utils/price-settings'

export const fromDynamicPricesDtoToModel = (dto: DynamicPricesDto): DynamicPricesModel => ({
  priceLimitWallbox: formatStringToNumberWithFallback(dto.priceLimitWb, 3),
  networkFees: formatStringToNumberWithFallback(dto.gridfee, 3),
  energyPriceSurcharge: formatStringToNumberWithFallback(dto.surcharge, 2),
  taxes: formatStringToNumberWithFallback(dto.furthertaxes, 3),
  dynamicPricesForWallboxesEnabled: dto.dppEnabledWb ?? false,
  dynamicPricesForBatteryEnabled: dto.dppEnabledBattery ?? false,
  isDynamicEmsEnabledForBattery: dto.enableDynamicEms ?? false,
  priceLimitBattery: formatStringToNumberWithFallback(dto.priceLimitBattery, 3),
  activeMonthsBattery: dto.activeMonthsBattery ?? [],
  batteryStateOfChargeLimit: formatStringToNumberWithFallback(dto.maxSocBattery, 0),
})

/***
 * Full set of dynamic power prices properties, supported by newer software versions.
 */
export const fromDynamicPricesModelFullVersionToDto = (model: DynamicPricesModel): DynamicPricesDto => {
  return {
    gridfee: Number(model.networkFees),
    surcharge: Number(Number(model.energyPriceSurcharge).toFixed(2)),
    furthertaxes: Number(model.taxes),
    dppEnabledWb: model.dynamicPricesForWallboxesEnabled,
    priceLimitWb: Number(model.priceLimitWallbox),
    dppEnabledBattery: model.dynamicPricesForBatteryEnabled ?? false,
    enableDynamicEms: Boolean(
      model.dynamicPricesForBatteryEnabled && model.isDynamicEmsEnabledForBattery
    ) /** dynamic ems can only be enabled if the dpp battery case is also enabled */,
    priceLimitBattery: Number(model.priceLimitBattery),
    activeMonthsBattery: model.activeMonthsBattery,
    maxSocBattery: Number(model.batteryStateOfChargeLimit),
  }
}

/***
 * Older software only includes a minimal set of dynamic power prices properties, which are considered here.
 */
export const fromDynamicPricesModelMinimalVersionToDto = (model: DynamicPricesModel): DynamicPricesDto => ({
  gridfee: Number(model.networkFees),
  surcharge: Number(model.energyPriceSurcharge),
  furthertaxes: Number(model.taxes),
  dppEnabledWb: model.dynamicPricesForWallboxesEnabled,
  priceLimitWb: Number(model.priceLimitWallbox),
})

/**
 * Transforms storage reference model of backend to model we want to use on frontend side.
 * Backend sends an array for all those values to store the history. The most recent entry is thus the current setting.
 */
export const fromReferencePriceDtoToModel = (dto: ReferencePriceDto): ReferencePriceModel => {
  return {
    referencePrice: dto.price[dto.price.length - 1].toFixed(2).toString() ?? '0',
  }
}

/**
 * Transform battery reference prices backend model to frontend model.
 */
export const fromReferencePriceModelToDto = (referencePrice: string | undefined): ReferencePricesPostDto => {
  return {
    prices: [
      {
        from: null,
        price: referencePrice ?? '0',
      },
    ],
  }
}

/**
 * Transforms dynamic EMS settings from backend to frontend model.
 */
export const fromDynamicEmsDtoToModel = (dto: DynamicEmsSettingsDto): DynamicEmsSettingsModel => ({
  isDynamicEmsEnabledForBattery: dto.battery ?? false,
  isSgReadyEnabled: dto.sgReady ?? false,
  feedInTariffType: dto.feedInTariffType,
  isActive: dto.isActive,
})

/**
 * Transforms dynamic EMS settings from frontend to backend model.
 */
export const fromDynamicEmsModelToDto = (model: DynamicEmsSettingsModel): DynamicEmsSettingsDto => ({
  battery: model.isDynamicEmsEnabledForBattery,
  sgReady: model.isSgReadyEnabled,
  feedInTariffType: model.feedInTariffType,
  isActive: model.isActive,
})

/**
 * Transforms dynamic EMS settings from frontend to backend model for PUT request.
 */
export const fromUpdateDynamicEmsModelToDto = (model: UpdateDynamicEmsSettingsModel): UpdateDynamicEmsSettingsDto => ({
  battery: model.isDynamicEmsEnabledForBattery,
  sgReady: model.isSgReadyEnabled,
  feedInTariffType: model.feedInTariffType,
})
